import { createContext, useContext, useEffect, useState } from "react";

const ThemeProviderContext = createContext({
  theme: "dark",
  setTheme: () => null,
});

export function ThemeProvider({
  children,
  defaultTheme = "dark",
  storageKey = "braincx-ui-theme",
  ...props
}) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") {
      return defaultTheme;
    }
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === "light" || stored === "dark") {
        return stored;
      }
      if (stored === "system") {
        localStorage.setItem(storageKey, "dark");
        return "dark";
      }
      // Check what's actually on the HTML element (set by blocking script)
      const htmlClass = document.documentElement.classList.contains("dark") ? "dark" : 
                       document.documentElement.classList.contains("light") ? "light" : null;
      if (htmlClass) {
        localStorage.setItem(storageKey, htmlClass);
        return htmlClass;
      }
      localStorage.setItem(storageKey, defaultTheme);
      return defaultTheme;
    } catch (e) {
      return defaultTheme;
    }
  });

  // Sync theme with DOM on mount and when theme changes
  useEffect(() => {
    if (typeof window === "undefined") return;
    
    const root = document.documentElement;
    const currentTheme = theme === "light" || theme === "dark" ? theme : defaultTheme;
    
    // Remove both classes
    root.classList.remove("light", "dark");
    // Add the current theme
    root.classList.add(currentTheme);
    
    // Ensure localStorage is in sync
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored !== currentTheme) {
        localStorage.setItem(storageKey, currentTheme);
      }
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [theme, defaultTheme, storageKey]);

  const setTheme = (newTheme) => {
    // Validate and normalize theme
    if (newTheme !== "light" && newTheme !== "dark") {
      newTheme = defaultTheme;
    }
    
    // Update state
    setThemeState(newTheme);
    
    // Immediately apply to DOM
    if (typeof window !== "undefined") {
      const root = document.documentElement;
      root.classList.remove("light", "dark");
      root.classList.add(newTheme);
      
      // Update localStorage
      try {
        localStorage.setItem(storageKey, newTheme);
      } catch (e) {
        // Ignore localStorage errors
      }
    }
  };

  const value = {
    theme: theme === "light" || theme === "dark" ? theme : defaultTheme,
    setTheme,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");
  return context;
};

